import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  HelpCircle,
  Search,
  MessageCircle,
  Phone,
  Mail,
  Clock,
  ChevronRight,
  CreditCard,
  Lock,
  Smartphone,
  DollarSign,
  TrendingUp,
  Settings,
  FileText,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Send,
  MapPin,
  Globe,
  Calendar,
  RefreshCw,
  Loader2,
  Check,
  X,
  Ticket,
  Plus
} from 'lucide-react'
import { SupportAPI } from '../services/api'

const categories = [
  {
    id: 'accounts',
    title: 'Accounts & Cards',
    icon: CreditCard,
    color: 'bg-blue-50 text-blue-600',
    articleCount: 12
  },
  {
    id: 'security',
    title: 'Security & Privacy',
    icon: Lock,
    color: 'bg-red-50 text-red-600',
    articleCount: 8
  },
  {
    id: 'mobile',
    title: 'Mobile Banking',
    icon: Smartphone,
    color: 'bg-purple-50 text-purple-600',
    articleCount: 10
  },
  {
    id: 'payments',
    title: 'Payments & Transfers',
    icon: DollarSign,
    color: 'bg-emerald-50 text-emerald-600',
    articleCount: 15
  },
  {
    id: 'investments',
    title: 'Investments',
    icon: TrendingUp,
    color: 'bg-orange-50 text-orange-600',
    articleCount: 9
  },
  {
    id: 'settings',
    title: 'Account Settings',
    icon: Settings,
    color: 'bg-slate-50 text-slate-600',
    articleCount: 7
  }
]

const popularArticles = [
  {
    id: 1,
    title: 'How do I reset my password?',
    category: 'Security & Privacy',
    views: '12.5K',
    helpful: 145
  },
  {
    id: 2,
    title: 'How to set up mobile deposit?',
    category: 'Mobile Banking',
    views: '10.2K',
    helpful: 132
  },
  {
    id: 3,
    title: 'What are the transfer limits?',
    category: 'Payments & Transfers',
    views: '9.8K',
    helpful: 121
  },
  {
    id: 4,
    title: 'How to enable two-factor authentication?',
    category: 'Security & Privacy',
    views: '8.3K',
    helpful: 98
  },
  {
    id: 5,
    title: 'How do I dispute a transaction?',
    category: 'Accounts & Cards',
    views: '7.9K',
    helpful: 87
  }
]

const faqs = [
  {
    question: 'How long does it take to open an account?',
    answer: 'You can open a new account in as little as 5 minutes online. Once submitted, most accounts are approved instantly, though some may require additional verification which can take 1-2 business days.'
  },
  {
    question: 'Are my deposits insured?',
    answer: 'Yes, all deposits are FDIC insured up to $250,000 per depositor. This means your money is protected by the full faith and credit of the United States government.'
  },
  {
    question: 'What fees does Swift Gold Fiduciary charge?',
    answer: 'We believe in transparent pricing. There are no monthly maintenance fees on most accounts, no minimum balance requirements, and we reimburse all ATM fees worldwide. Check our fee schedule for specific details.'
  },
  {
    question: 'Can I use my card internationally?',
    answer: 'Yes, your Swift Gold Fiduciary card works in over 200 countries worldwide. We don\'t charge foreign transaction fees, and you get competitive exchange rates. Just let us know your travel plans to ensure uninterrupted access.'
  },
  {
    question: 'How do I report a lost or stolen card?',
    answer: 'Immediately lock your card in the mobile app or call our 24/7 hotline at 1-800-SECURE-1. You can also report it through online banking. We\'ll overnight a replacement card at no charge.'
  },
  {
    question: 'What is the daily ATM withdrawal limit?',
    answer: 'The standard daily ATM withdrawal limit is $1,000. You can request a temporary or permanent limit increase through the mobile app or by contacting customer service.'
  }
]

const contactMethods = [
  {
    id: 'chat',
    title: 'Live Chat',
    description: 'Chat with our support team in real-time',
    icon: MessageCircle,
    color: 'bg-blue-50 text-blue-600',
    availability: 'Available 24/7',
    responseTime: 'Avg. 2 min response'
  },
  {
    id: 'phone',
    title: 'Phone Support',
    description: 'Speak directly with a representative',
    icon: Phone,
    color: 'bg-emerald-50 text-emerald-600',
    availability: '1-800-SECURE-1',
    responseTime: '24/7 availability'
  },
  {
    id: 'email',
    title: 'Email Support',
    description: 'Send us a detailed message',
    icon: Mail,
    color: 'bg-purple-50 text-purple-600',
    availability: 'support@securebank.com',
    responseTime: 'Response within 24h'
  },
  {
    id: 'branch',
    title: 'Visit a Branch',
    description: 'Get help at a physical location',
    icon: MapPin,
    color: 'bg-orange-50 text-orange-600',
    availability: 'Find nearest branch',
    responseTime: 'Mon-Fri 9AM-5PM'
  }
]

const quickLinks = [
  { title: 'Update personal information', icon: FileText },
  { title: 'Order checks', icon: CheckCircle },
  { title: 'Request account statements', icon: Calendar },
  { title: 'Change address', icon: MapPin },
  { title: 'Set up direct deposit', icon: DollarSign },
  { title: 'Report fraud', icon: AlertCircle }
]

export default function HelpPage() {
  const [activeTab, setActiveTab] = useState('browse')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [expandedFaq, setExpandedFaq] = useState(null)

  // Data states
  const [faqData, setFaqData] = useState(faqs)
  const [tickets, setTickets] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const [searchResults, setSearchResults] = useState([])

  // UI states
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [showMyTickets, setShowMyTickets] = useState(false)

  // Form state
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    message: '',
    category: ''
  })

  // Fetch data on mount
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [faqsRes, ticketsRes, announcementsRes] = await Promise.all([
        SupportAPI.getFaqs(),
        SupportAPI.getTickets(),
        SupportAPI.getAnnouncements()
      ])

      if (faqsRes.success && faqsRes.data?.length > 0) {
        setFaqData(faqsRes.data.map(f => ({
          question: f.question,
          answer: f.answer,
          id: f.id,
          category: f.category,
          helpful: f.helpful_count || 0
        })))
      }

      if (ticketsRes.success) {
        setTickets(ticketsRes.data || [])
      }

      if (announcementsRes.success) {
        setAnnouncements(announcementsRes.data || [])
      }
    } catch (err) {
      console.error('Error fetching support data:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Search FAQs
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([])
      return
    }
    try {
      const res = await SupportAPI.searchFaqs(query)
      if (res.success) {
        setSearchResults(res.data || [])
      }
    } catch (err) {
      console.error('Search error:', err)
    }
  }

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const showSuccess = (message) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  // Submit support ticket
  const handleSubmitTicket = async () => {
    if (!ticketForm.subject || !ticketForm.message) {
      setError('Please fill in all required fields')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await SupportAPI.createTicket({
        subject: ticketForm.subject,
        message: ticketForm.message,
        category: ticketForm.category || 'general'
      })
      if (res.success) {
        setShowTicketModal(false)
        setTicketForm({ subject: '', message: '', category: '' })
        showSuccess('Support ticket submitted successfully')
        fetchData()
      } else {
        setError(res.message || 'Failed to submit ticket')
      }
    } catch (err) {
      setError(err.message || 'Failed to submit ticket')
    } finally {
      setSaving(false)
    }
  }

  // Rate FAQ as helpful
  const handleRateFaq = async (id, helpful) => {
    try {
      await SupportAPI.rateFaq(id, helpful)
      showSuccess(helpful ? 'Thanks for your feedback!' : 'Thanks for letting us know')
    } catch (err) {
      console.error('Rating error:', err)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Help Center</h1>
              <p className="text-sm text-slate-500 mt-0.5">Find answers and get support</p>
            </div>

            <div className="flex items-center gap-3">
              {tickets.length > 0 && (
                <button
                  onClick={() => setShowMyTickets(true)}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 text-sm font-medium"
                >
                  <Ticket className="w-4 h-4" />
                  My Tickets ({tickets.length})
                </button>
              )}
              <button
                onClick={() => setShowTicketModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                Contact Support
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2"
            >
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">{successMessage}</span>
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">&times;</button>
            </motion.div>
          )}
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for help articles, guides, and FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-slate-900 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 bg-slate-100 rounded-lg p-1 mb-8">
          {['browse', 'popular', 'faq', 'contact'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 rounded-md transition-colors capitalize text-sm font-medium ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Browse by Category Tab */}
        {activeTab === 'browse' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-sm font-semibold text-slate-900 mb-6">Browse by Category</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category, index) => {
                  const IconComponent = category.icon
                  return (
                    <motion.button
                      key={category.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => setSelectedCategory(category)}
                      className="p-5 bg-white border border-slate-200 rounded-xl hover:border-slate-900 transition-all text-left group"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-lg ${category.color} flex items-center justify-center`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-900 transition-colors" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{category.title}</h3>
                      <p className="text-xs text-slate-500">{category.articleCount} articles</p>
                    </motion.button>
                  )
                })}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h2 className="text-sm font-semibold text-slate-900 mb-6">Quick Actions</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {quickLinks.map((link, index) => {
                  const IconComponent = link.icon
                  return (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                    >
                      <IconComponent className="w-5 h-5 text-slate-600" />
                      <span className="text-sm text-slate-900">{link.title}</span>
                    </motion.button>
                  )
                })}
              </div>
            </div>

            {/* Status Banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-emerald-900 mb-1">All Systems Operational</h3>
                  <p className="text-xs text-emerald-700 mb-3">
                    All banking services are running normally. Last checked 2 minutes ago.
                  </p>
                  <button className="flex items-center gap-2 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                    View system status
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Popular Articles Tab */}
        {activeTab === 'popular' && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-slate-900 mb-6">Most Popular Articles</h2>
            {popularArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-5 bg-white border border-slate-200 rounded-xl hover:border-slate-900 transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-slate-900 mb-2 group-hover:text-slate-700">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {article.views} views
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {article.helpful} found helpful
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-900 transition-colors" />
                </div>
              </motion.div>
            ))}

            {/* Helpful Resources */}
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-sm font-semibold text-blue-900 mb-2">User Guides</h3>
                <p className="text-xs text-blue-700 mb-4">
                  Comprehensive guides covering all features and services
                </p>
                <button className="flex items-center gap-2 text-xs font-medium text-blue-700 hover:text-blue-800">
                  Browse guides
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>

              <div className="p-6 bg-purple-50 border border-purple-200 rounded-xl">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Smartphone className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-sm font-semibold text-purple-900 mb-2">Video Tutorials</h3>
                <p className="text-xs text-purple-700 mb-4">
                  Step-by-step video guides to get you started quickly
                </p>
                <button className="flex items-center gap-2 text-xs font-medium text-purple-700 hover:text-purple-800">
                  Watch tutorials
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-slate-900 mb-6">Frequently Asked Questions</h2>
            {faqData.map((faq, index) => (
              <motion.div
                key={faq.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-3 text-left">
                    <HelpCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900">{faq.question}</span>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ${
                    expandedFaq === index ? 'rotate-90' : ''
                  }`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-5 pb-5 pl-14">
                    <p className="text-xs text-slate-600 leading-relaxed mb-4">{faq.answer}</p>
                    {faq.id && (
                      <div className="flex items-center gap-4 pt-3 border-t border-slate-100">
                        <span className="text-xs text-slate-500">Was this helpful?</span>
                        <button
                          onClick={() => handleRateFaq(faq.id, true)}
                          className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Yes
                        </button>
                        <button
                          onClick={() => handleRateFaq(faq.id, false)}
                          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                        >
                          <X className="w-3 h-3" /> No
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ))}

            {/* Still have questions */}
            <div className="mt-8 bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Didn't find what you're looking for?</h3>
              <p className="text-xs text-slate-500 mb-4">
                Our support team is here to help you with any questions
              </p>
              <button
                onClick={() => setShowTicketModal(true)}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium"
              >
                Ask a Question
              </button>
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-sm font-semibold text-slate-900 mb-6">How would you like to reach us?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {contactMethods.map((method, index) => {
                  const IconComponent = method.icon
                  return (
                    <motion.div
                      key={method.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-6 bg-white border border-slate-200 rounded-xl hover:border-slate-900 transition-all cursor-pointer group"
                    >
                      <div className={`w-12 h-12 rounded-lg ${method.color} flex items-center justify-center mb-4`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">{method.title}</h3>
                      <p className="text-xs text-slate-500 mb-4">{method.description}</p>
                      <div className="space-y-1 mb-4">
                        <p className="text-xs font-medium text-slate-700">{method.availability}</p>
                        <p className="text-xs text-slate-500">{method.responseTime}</p>
                      </div>
                      <button className="flex items-center gap-2 text-sm font-medium text-slate-900 group-hover:text-slate-700">
                        Get Started
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )
                })}
              </div>
            </div>

            {/* Support Hours */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Support Hours</h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between gap-8">
                      <span className="text-slate-500">Phone & Chat Support:</span>
                      <span className="text-slate-900 font-medium">24/7</span>
                    </div>
                    <div className="flex justify-between gap-8">
                      <span className="text-slate-500">Email Support:</span>
                      <span className="text-slate-900 font-medium">24/7 (Response within 24h)</span>
                    </div>
                    <div className="flex justify-between gap-8">
                      <span className="text-slate-500">Branch Locations:</span>
                      <span className="text-slate-900 font-medium">Mon-Fri 9AM-5PM, Sat 9AM-1PM</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Send us a message */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Send us a message</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2">Subject</label>
                  <input
                    type="text"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="What can we help you with?"
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2">Message</label>
                  <textarea
                    rows={4}
                    value={ticketForm.message}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Describe your issue or question..."
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900 resize-none"
                  />
                </div>
                <button
                  onClick={handleSubmitTicket}
                  disabled={saving || !ticketForm.subject || !ticketForm.message}
                  className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showTicketModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Contact Support</h2>
              <button
                onClick={() => setShowTicketModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Category</label>
                <select
                  value={ticketForm.category}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                >
                  <option value="">Select a category</option>
                  <option value="accounts">Accounts & Cards</option>
                  <option value="security">Security & Privacy</option>
                  <option value="payments">Payments & Transfers</option>
                  <option value="mobile">Mobile Banking</option>
                  <option value="technical">Technical Support</option>
                  <option value="general">General Question</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Subject *</label>
                <input
                  type="text"
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Brief description of your issue"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Message *</label>
                <textarea
                  rows={4}
                  value={ticketForm.message}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Please describe your issue in detail..."
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowTicketModal(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:border-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitTicket}
                disabled={saving || !ticketForm.subject || !ticketForm.message}
                className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit Ticket
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* My Tickets Modal */}
      {showMyTickets && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">My Support Tickets</h2>
              <button
                onClick={() => setShowMyTickets(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="space-y-3">
              {tickets.length === 0 ? (
                <div className="text-center py-8">
                  <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No support tickets yet</p>
                </div>
              ) : (
                tickets.map((ticket) => (
                  <div key={ticket.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-slate-900">{ticket.subject}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                        ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {ticket.status?.replace('_', ' ').toUpperCase() || 'OPEN'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2 line-clamp-2">{ticket.message}</p>
                    <p className="text-xs text-slate-400">
                      Created {new Date(ticket.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <button
                onClick={() => {
                  setShowMyTickets(false)
                  setShowTicketModal(true)
                }}
                className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Ticket
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
